// One tachograph file: what it is about (summary), where it is filed, and its daily activities
// as a 24-hour chart per day. Times are UTC, as the tachograph records them.
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  api, Customer, customers as customersApi, tacho, TachoDay, TachoDecoded, TachoFile as TachoFileRow, TachoInfringement, Vehicle,
} from '../api';
import { useAuth } from '../auth';
import { Link } from '../components/Links';
import { SearchSelect } from '../components/SearchSelect';
import { TachoDays } from '../components/TachoDays';
import { dateTime } from '../format';
import { GENERATION_LABEL, KIND_LABEL, periodText, subjectText } from './TachoFiles';

const utc = (iso: string | null | undefined) => (iso ? `${new Date(iso).toLocaleString('en-IE', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} UTC` : '–');
const dayOf = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('en-IE', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' }) : '–');
const dash = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '–' : v);

function Summary({ file, decoded }: { file: TachoFileRow; decoded: TachoDecoded }) {
  if (decoded.kind === 'card') {
    const c = decoded.card;
    const expired = c.expires && Date.parse(c.expires) < Date.now();
    return (
      <section className="card" aria-label="Summary">
        <h2>Driver card</h2>
        <dl className="facts">
          <dt>Driver</dt><dd>{[c.firstNames, c.surname].filter(Boolean).join(' ') || '–'}</dd>
          <dt>Date of birth</dt><dd>{c.birthDate ? dayOf(`${c.birthDate}T00:00:00Z`) : '–'}</dd>
          <dt>Card number</dt><dd>{c.number}{c.nation && <span className="muted"> · {c.nation}</span>}</dd>
          <dt>Card valid</dt><dd>{dayOf(c.validFrom)} – <span className={expired ? 'error' : undefined}>{dayOf(c.expires)}{expired ? ' (expired)' : ''}</span></dd>
          <dt>Issued by</dt><dd>{dash(c.issuingAuthority)}</dd>
          <dt>Driving licence</dt><dd>{c.licence ? <>{c.licence.number}{c.licence.nation && <span className="muted"> · {c.licence.nation}</span>}</> : '–'}</dd>
          <dt>Tachograph</dt><dd>{GENERATION_LABEL[decoded.generation]} card</dd>
          <dt>Activities</dt><dd>{periodText(decoded.period.from, decoded.period.to)}</dd>
          <dt>Downloaded</dt><dd>{file.downloadedAt ? utc(file.downloadedAt) : '–'}</dd>
          <dt>Card last downloaded</dt><dd>{utc(c.lastDownload)}</dd>
          <dt>Vehicles used</dt>
          <dd>{decoded.vehiclesUsed.length
            ? [...new Set(decoded.vehiclesUsed.map((v) => v.reg))].slice(-12).reverse().join(', ')
            : '–'}</dd>
        </dl>
      </section>
    );
  }
  const { vehicle: v, unit: u } = decoded;
  return (
    <section className="card" aria-label="Summary">
      <h2>Vehicle unit</h2>
      <dl className="facts">
        <dt>Registration</dt><dd>{dash(v.reg)}{v.nation && <span className="muted"> · {v.nation}</span>}</dd>
        <dt>VIN</dt><dd>{dash(v.vin)}</dd>
        <dt>Tachograph</dt><dd>{[u.manufacturer, GENERATION_LABEL[decoded.generation]].filter(Boolean).join(' · ')}</dd>
        <dt>Serial / part</dt><dd>{dash(u.serialNumber)}{u.partNumber && <span className="muted"> · {u.partNumber}</span>}{u.softwareVersion && <span className="muted"> · software {u.softwareVersion}</span>}</dd>
        <dt>Activities</dt><dd>{periodText(decoded.period.from, decoded.period.to)}</dd>
        <dt>Unit's time at download</dt><dd>{utc(u.currentTime)}</dd>
        <dt>Data held in the unit</dt><dd>{dayOf(u.downloadableFrom)} – {dayOf(u.downloadableTo)}</dd>
        <dt>Previous download</dt><dd>{u.lastDownload ? <>{utc(u.lastDownload.at)}{u.lastDownload.by && <span className="muted"> · {u.lastDownload.by}</span>}</> : '–'}</dd>
        <dt>Company locks</dt>
        <dd>{u.companyLocks.length ? u.companyLocks.map((l, i) => (
          <div key={i}>{l.company || l.cardNumber} <span className="muted">· from {dayOf(l.from)}{l.to ? ` to ${dayOf(l.to)}` : ', still locked'}</span></div>
        )) : 'None'}</dd>
      </dl>
    </section>
  );
}

/** Customer and vehicle the file belongs to; staff can change them. */
function Filing({ file, onChange }: { file: TachoFileRow; onChange: (f: TachoFileRow) => void }) {
  const [custs, setCusts] = useState<Customer[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [error, setError] = useState('');
  useEffect(() => { customersApi.list().then((r) => setCusts(r.customers)).catch(() => {}); }, []);
  useEffect(() => { if (file.kind === 'vu') api.vehicles().then((r) => setVehicles(r.vehicles)).catch(() => {}); }, [file.kind]);
  const save = (body: { customerId?: number | null; vehicleId?: number | null }) =>
    tacho.file(file.id, body).then((r) => { onChange(r.file); setError(''); }).catch((e) => setError((e as Error).message));
  return (
    <section className="card form" aria-label="Filed under">
      <h2>Filed under</h2>
      <div className="tacho-filing">
        <label htmlFor="tacho-file-customer">Customer</label>
        <SearchSelect id="tacho-file-customer" value={file.customerId ? String(file.customerId) : ''} emptyLabel="– none yet –"
          options={custs.map((c) => ({ value: String(c.id), label: c.name }))}
          onChange={(v) => void save({ customerId: v ? Number(v) : null })} />
        {file.kind === 'vu' && <>
          <label htmlFor="tacho-file-vehicle">Vehicle</label>
          <SearchSelect id="tacho-file-vehicle" value={file.vehicleId ? String(file.vehicleId) : ''} emptyLabel="– not one of ours –"
            options={vehicles.map((v) => ({ value: String(v.id), label: v.name, detail: v.registration ?? undefined }))}
            onChange={(v) => void save({ vehicleId: v ? Number(v) : null })} />
        </>}
      </div>
      {file.kind === 'vu' && file.vehicleId && <p className="small"><Link to={`/vehicles/${file.vehicleId}`}>Open {file.vehicleName ?? 'the vehicle'} →</Link></p>}
      {error && <p className="error" role="alert">{error}</p>}
    </section>
  );
}

export function TachoFile() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const { state } = useLocation() as { state: { duplicate?: boolean } | null };
  const { user } = useAuth();
  const [file, setFile] = useState<TachoFileRow | null>(null);
  const [decoded, setDecoded] = useState<TachoDecoded | null>(null);
  const [byDay, setByDay] = useState<Record<string, TachoInfringement[]>>({});
  const [drivers, setDrivers] = useState<Array<{ id: number; name: string; role: 'holder' | 'vu' }>>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    tacho.get(id).then((r) => {
      setFile(r.file); setDecoded(r.decoded); setByDay(r.infringements ?? {}); setDrivers(r.drivers ?? []); setError(r.error ?? '');
    }).catch((e) => setError((e as Error).message));
  }, [id]);
  const dayInfringements = useCallback((d: TachoDay) => byDay[d.date] ?? [], [byDay]);

  const del = async () => {
    if (!file || !window.confirm(`Delete ${file.fileName} from the archive? Tachograph files are legal records; only delete one uploaded by mistake.`)) return;
    try { await tacho.remove(file.id); navigate('/tacho'); } catch (e) { setError((e as Error).message); }
  };

  if (!file) return <div className="page"><p><Link to="/tacho">← Tachograph files</Link></p>{error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>}</div>;
  return (
    <div className="page">
      <p><Link to="/tacho">← Tachograph files</Link></p>
      <div className="page-head">
        <div>
          <h1>{subjectText(file)}</h1>
          <p className="muted small">{KIND_LABEL[file.kind]} · {GENERATION_LABEL[file.generation]} · {file.fileName}
            {' '}· {file.source === 'remote' ? 'remote download' : `uploaded${file.uploadedBy ? ` by ${file.uploadedBy}` : ''}${file.uploadedAt ? ` ${dateTime(file.uploadedAt)}` : ''}`}</p>
        </div>
        <div className="toolbar">
          <a className="button ghost small" href={tacho.downloadUrl(file.id)} download>Download .ddd</a>
          {user?.role === 'admin' && <button type="button" className="button ghost small danger" onClick={() => void del()}>Delete</button>}
        </div>
      </div>
      {state?.duplicate && <p className="notice">This file was already in the archive, so it was not added again.</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {decoded?.warnings.map((w) => <p key={w} className="notice">{w}</p>)}
      {decoded && <Summary file={file} decoded={decoded} />}
      <Filing file={file} onChange={setFile} />
      {drivers.length > 0 && (
        <p className="small">{decoded?.kind === 'card' ? 'Driver: ' : 'Drivers in this file: '}
          {drivers.map((d, i) => <span key={d.id}>{i ? ', ' : ''}<Link to={`/tacho/drivers/${d.id}`}>{d.name}</Link></span>)}
          {decoded?.kind === 'card' && <span className="muted"> – checks use all of this driver's files</span>}
        </p>
      )}
      {decoded && (
        <TachoDays days={decoded.days} vu={decoded.kind === 'vu'} infringements={dayInfringements}
          period={periodText(decoded.period.from, decoded.period.to)}
          extraNote={decoded.kind === 'vu' ? 'Drivers\' hours are checked per driver – open a driver above. This file is checked for driving without a card.' : undefined} />
      )}
      {decoded && !decoded.signaturesChecked && (
        <p className="muted small">The file's digital signatures are not checked yet, so this view does not prove the file is untouched. Keep the original .ddd file as the legal record.</p>
      )}
    </div>
  );
}
